import { OUTBOUND_CALL_OUTCOME_LABELS } from '../constants/outbound-call-outcome';
import {
  AUDIENCE_CONDITION_LABELS,
  audienceParamFields,
} from '../schemas/outreach-audience-fields';
import {
  audienceCheckedFor,
  campaignPlacesCalls,
  stepHoursWithin,
} from '../schemas/outreach-campaign';
import type {
  Audience,
  AudienceFilterField,
  CampaignSpec,
  OutreachWindow,
  ReferralReadiness,
  SequenceStep,
  StepSlot,
  SuccessCondition,
  TimingBand,
} from '../schemas/outreach-campaign';

/**
 * The campaign, in English.
 *
 * **Deterministic and pure — no model is involved in this direction.** The
 * editor shows this text beside the sequence as a second view of the same
 * document, so it has to be free to re-render on every keystroke and it has to
 * be impossible for it to disagree with the spec. A generated description that
 * can drift from the thing it describes is worse than none: an author would
 * read it, believe it, and publish something else.
 *
 * The reverse direction — English back into a spec — is a compiler, is the only
 * lossy half, and lives in the API. It is given both the author's text AND the
 * current spec precisely because this renderer is not a serialization: a spec
 * carries settings that no sentence here mentions (message slots, per-step
 * windows), and those must survive a round trip rather than being dropped by
 * anything they were never expressible in.
 *
 * Consequences that must hold, in order of how badly they bite:
 *
 *  1. **Everything the spec says, this says.** A field with no prose of its own
 *     still renders, from its schema-derived label, rather than being silently
 *     omitted. Omission is how the text starts lying.
 *  2. **It says nothing the spec does not.** No advice, no warnings, no derived
 *     judgements — those belong to the editor, which has {@link plannedAttempts}
 *     and the simulator for exactly that. Prose the compiler reads back must
 *     contain only instructions.
 *  3. **Sentences carry stable ids.** The panel anchors a click to the control
 *     that owns the clause, and the compiler's change summary names them.
 */

// ───────────────────────────── small helpers ─────────────────────────────

/** `1 day`, `6 months`. */
export function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

/** `a`, `a and b`, `a, b, or c`. */
function joinList(items: readonly string[], conjunction: 'and' | 'or'): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0]!;
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, ${conjunction} ${items[items.length - 1]}`;
}

const HOUR = 60;
const DAY = 24 * HOUR;

/**
 * `4 hours`, `1 day`, `straight away`.
 *
 * Digits rather than words throughout. This text is read as configuration and
 * scanned for numbers, and in the reverse direction a digit is one less thing
 * for the compiler to interpret.
 */
export function formatDuration(minutes: number): string {
  if (minutes <= 0) return 'straight away';
  if (minutes % DAY === 0) return plural(minutes / DAY, 'day');
  if (minutes % HOUR === 0) return plural(minutes / HOUR, 'hour');
  return plural(minutes, 'minute');
}

const DAY_NAMES: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};
const SHORT_DAYS: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};
const DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

/** `Mon–Fri`, `Mon, Wed, Fri`, `every day` — the compact form, for chips. */
export function describeDays(days: readonly string[]): string {
  const sorted = DAY_ORDER.filter((d) => days.includes(d));
  if (sorted.length === 0) return 'no days';
  if (sorted.length === 7) return 'every day';

  // Contiguous in week order reads as a range; anything else is listed.
  const first = DAY_ORDER.indexOf(sorted[0]!);
  const contiguous = sorted.every((d, i) => DAY_ORDER.indexOf(d) === first + i);
  if (contiguous && sorted.length > 2) {
    return `${SHORT_DAYS[sorted[0]!]}–${SHORT_DAYS[sorted[sorted.length - 1]!]}`;
  }
  return sorted.map((d) => SHORT_DAYS[d]).join(', ');
}

/** `Monday to Friday`, `Monday, Wednesday and Friday` — the prose form. */
function describeDaysLong(days: readonly string[]): string {
  const sorted = DAY_ORDER.filter((d) => days.includes(d));
  if (sorted.length === 0) return 'no days';
  if (sorted.length === 7) return 'every day';

  const first = DAY_ORDER.indexOf(sorted[0]!);
  const contiguous = sorted.every((d, i) => DAY_ORDER.indexOf(d) === first + i);
  if (contiguous && sorted.length > 2) {
    return `${DAY_NAMES[sorted[0]!]} to ${DAY_NAMES[sorted[sorted.length - 1]!]}`;
  }
  return joinList(
    sorted.map((d) => DAY_NAMES[d]!),
    'and'
  );
}

/**
 * Outcomes as clauses that complete "if …" or "as soon as …".
 *
 * **Not the picker labels.** Those are chips — "Spoke, no action",
 * "Disconnected / unavailable" — and splicing them into a sentence produced
 * "the run stops as soon as a call ends as scheduled, declined, wrong number,
 * spoke, no action, or transferred to staff", where one label's own comma
 * silently breaks the list into two items. A chip and a clause are different
 * jobs; the chip stays in the picker, this reads.
 *
 * One table serves both uses because both grammatical contexts are "if this
 * happened", which is what an outcome is.
 */
const OUTCOME_PROSE: Record<string, string> = {
  scheduled: 'they book',
  engaged_no_booking: 'we speak to them without booking',
  declined: 'they decline',
  callback_requested: 'they ask to be called back',
  wrong_number: 'it turns out to be the wrong number',
  voicemail: 'we reach voicemail',
  no_answer: 'nobody answers',
  unavailable: 'the number is unavailable',
  failed: 'the call fails to connect',
  escalated: 'the call is handed to a member of staff',
};

function outcomeWords(values: readonly string[]): string {
  const clauses = values.map((value) => {
    const prose = OUTCOME_PROSE[value];
    if (prose) return prose;

    // An outcome this build has no prose for is rendered from its picker label,
    // and one it does not know at all from its raw value. Never dropped:
    // `continueOn` is a plain string array by design, so an unfamiliar value is
    // a spec from a newer build, and omitting it would understate what the
    // campaign does to real patients.
    const label = OUTBOUND_CALL_OUTCOME_LABELS.find((o) => o.value === value)?.label;
    return `the call ends as "${label ?? value}"`;
  });
  return joinList(clauses, 'or');
}

// ───────────────────────────── name resolution ─────────────────────────────

/**
 * Display names for filter values, which are ids in the spec.
 *
 * Optional, because this module is called from the API too, where the location
 * and provider tables are a query away and the text is wanted anyway. An
 * unresolved id degrades to a count — `2 locations` — never to a raw uuid,
 * which would be noise in a sentence and meaningless to the compiler.
 */
export interface DescribeOptions {
  names?: Readonly<Record<string, string>>;
  /**
   * Describe who is actually CHECKED, not only what was authored: a campaign
   * that places calls needs a date of birth whatever its switch says (ADR-333).
   *
   * FOR DISPLAY ONLY, and off by default. `campaignText` and `diffCampaign`
   * always describe the spec as STORED: that text is what the author edits and
   * what the compiler reads back, so a sentence the spec does not hold would
   * round-trip into it, and a step changing channel would show up as an edit
   * to the audience.
   */
  asChecked?: boolean;
}

const FILTER_PREPOSITION: Record<AudienceFilterField, string> = {
  location: 'at',
  provider: 'with',
  appointmentType: 'for',
};
const FILTER_NOUN: Record<AudienceFilterField, string> = {
  location: 'location',
  provider: 'provider',
  appointmentType: 'appointment type',
};

/** `at Fremont or Oakland, with Dr Sahai` — or `` when nothing is narrowed. */
function describeFilters(audience: Audience, options: DescribeOptions): string {
  const names = options.names ?? {};

  const phrases = audience.filters.map((criterion) => {
    const resolved = criterion.values.map((id) => names[id]).filter((n): n is string => !!n);

    // All-or-nothing per criterion: a half-resolved list ("Fremont or 1 other")
    // reads as a different audience than it is.
    const subject =
      resolved.length === criterion.values.length
        ? joinList(resolved, 'or')
        : plural(criterion.values.length, FILTER_NOUN[criterion.field]);

    return `${FILTER_PREPOSITION[criterion.field]} ${subject}`;
  });

  return phrases.join(', ');
}

// ───────────────────────────── audience ─────────────────────────────

/**
 * A whole sentence per audience parameter.
 *
 * Sentences rather than clauses spliced into one long line: the connectives
 * that would join them ("with", "and", "who have") differ per parameter and per
 * condition, and getting them wrong produces text that is grammatical but means
 * something else. Short declaratives are also what the compiler round-trips
 * most reliably, and they give the panel one clickable anchor per control.
 *
 * Keys are unique across the whole catalog — the same property that lets
 * `PARAM_COPY` be one flat table — so this is keyed the same way. A parameter
 * with no entry here still renders, from its schema-derived label; see the
 * fallback in {@link describeAudience}.
 */
const PARAM_PROSE: Record<
  string,
  { number?: (value: number) => string; on?: string; off?: string }
> = {
  registeredWithinDays: {
    number: (v) => `They must have registered in the last ${plural(v, 'day')}.`,
  },
  openForAtLeastDays: {
    number: (v) =>
      v === 0
        ? 'A referral is called as soon as it is ready, however recently it arrived.'
        : `The referral must have been open for at least ${plural(v, 'day')}.`,
  },
  excludeIfBooked: {
    on: 'A referral drops out of the audience once something is booked against it.',
    off: 'A referral stays in the audience even once something is booked against it.',
  },
  noVisitForMonths: {
    number: (v) => `Nobody has seen them for ${plural(v, 'month')} or more.`,
  },
  includeAlreadyBooked: {
    on: 'Anyone who already has something booked is included too.',
    off: 'Anyone who already has something booked is left out.',
  },
  daysAhead: {
    number: (v) => `Their appointment must be within the next ${plural(v, 'day')}.`,
  },
  includeConfirmed: {
    on: 'Appointments that are already confirmed are included.',
    off: 'Appointments that are already confirmed are left out.',
  },
  daysAfterVisit: {
    number: (v) => `It reaches them ${plural(v, 'day')} after their visit.`,
  },
  withinDays: {
    number: (v) => `They must have missed an appointment in the last ${plural(v, 'day')}.`,
  },
};

function describeAudience(
  audience: Audience,
  options: DescribeOptions,
  callsNeedDob = false
): CampaignSentence[] {
  const label = AUDIENCE_CONDITION_LABELS[audience.type].toLowerCase();
  const filters = describeFilters(audience, options);

  const sentences: CampaignSentence[] = [
    {
      id: 'audience.condition',
      text: filters ? `Calls ${label} ${filters}.` : `Calls ${label}.`,
    },
  ];

  const params = audience.params as Record<string, number | boolean>;

  // Driven by the schema's own field list, not by the keys present on the
  // object: a parameter the spec somehow lacks is a spec that would not parse,
  // and iterating the schema keeps this in step with the catalog for free.
  for (const field of audienceParamFields(audience.type)) {
    const value = params[field.key];
    const prose = PARAM_PROSE[field.key];
    const id = `audience.param.${field.key}`;

    if (field.kind === 'toggle') {
      const on = value === true;
      const text = (on ? prose?.on : prose?.off) ?? `${field.label}: ${on ? 'yes' : 'no'}.`;
      sentences.push({ id, text });
      continue;
    }

    const numeric = typeof value === 'number' ? value : field.min;
    sentences.push({
      id,
      text: prose?.number?.(numeric) ?? `${field.label} ${numeric} ${field.unit}.`.trim(),
    });
  }

  const checked = audienceCheckedFor(audience, callsNeedDob);
  if (checked.type === 'open_referral') {
    sentences.push({ id: 'audience.readiness', text: describeReadiness(checked.readiness) });
  } else if (callsNeedDob) {
    // No switches to describe, but the same rule: nobody is called without one.
    sentences.push({
      id: 'audience.readiness',
      text: 'A patient is only called once they have a date of birth on file.',
    });
  }

  return sentences;
}

/**
 * When a referral counts as ready to call (ADR-318). Always rendered for a
 * referral audience, not only when a switch is off: it decides who is EVER
 * called, so an author must be able to read it whatever its values.
 */
function describeReadiness(readiness: ReferralReadiness): string {
  return `A referral is only called once it has ${joinList(readinessNeeds(readiness), 'and')}.`;
}

/**
 * What {@link describeReadiness} lists, one phrase per requirement
 * (`a name`, `a date of birth on file`), for a view that shows them apart.
 */
export function readinessNeeds(readiness: ReferralReadiness): string[] {
  const needs = ['a name', 'a phone number we can dial'];
  if (readiness.dob) needs.push('a date of birth on file');
  if (readiness.pool) needs.push('a provider who could see them');
  if (readiness.slots) {
    needs.push(
      `open slots with ${readiness.pool ? 'that provider' : 'any provider'} in the next ${plural(readiness.slotHorizonDays, 'day')}`
    );
  }
  return needs;
}

const TIMING_BAND_WORDS: Record<TimingBand, string> = {
  morning: 'in the morning',
  afternoon: 'in the early afternoon',
  late_afternoon: 'in the late afternoon',
  any: 'at any time in the calling hours',
};

/** Each slot as words: a band's phrase, or its custom hours. */
function slotWords(slot: StepSlot): string {
  if (slot.band !== 'custom') return TIMING_BAND_WORDS[slot.band];
  return slot.hours
    ? `between ${slot.hours.start} and ${slot.hours.end}`
    : 'at hours not chosen yet';
}

/**
 * A step's `timing`, or null when it has none (it then reads the old fields).
 * Automatic's slots are cut to the campaign's hours; the practice's quiet
 * hours may cut them further at call time.
 */
function describeTiming(step: SequenceStep, window: OutreachWindow): string | null {
  if (!step.timing) return null;
  const rotates = ', moving off the slot the last try used when another opens later that day';
  if (step.timing.mode === 'auto') {
    const { hours, fallsBack } = stepHoursWithin(step.timing, window);
    return fallsBack
      ? 'Its calls go out at any time in the calling hours.'
      : `Its calls go out ${hours.map((h) => `${h.start}–${h.end}`).join(' or ')}, the hours people most often pick up${rotates}.`;
  }
  const days = step.timing.days ? `, on ${describeDaysLong(step.timing.days)} only` : '';
  const when = step.timing.slots.map(slotWords).join(' or ');
  return `Its calls go out ${when}${days}${step.timing.slots.length > 1 ? rotates : ''}.`;
}

// ───────────────────────────── sequence ─────────────────────────────

/** `call`/`calls`, `text`/`texts` — the step's own verb, never assumed. */
function verb(step: SequenceStep, form: 'base' | 's'): string {
  const stem = step.channel === 'sms' ? 'text' : 'call';
  return form === 's' ? `${stem}s` : stem;
}

/** `twice, 4 hours apart`, `once`, `3 times, 1 day apart`. */
function describeRepeat(step: SequenceStep): string {
  const { times, spacingMinutes } = step.repeat;
  if (times <= 1) return 'once';

  const count = times === 2 ? 'twice' : `${times} times`;
  if (spacingMinutes <= 0) return `${count}, back to back`;
  return `${count}, ${formatDuration(spacingMinutes)} apart`;
}

/** `as soon as they qualify`, `1 day later` — sentence-medial. */
export function describeTriggerPhrase(step: SequenceStep): string {
  if (step.trigger.kind === 'qualifies') return 'as soon as they qualify';
  return step.trigger.minutes === 0
    ? 'immediately afterwards'
    : `${formatDuration(step.trigger.minutes)} later`;
}

/** The step's own label, or a positional stand-in when it has none. */
export function stepName(step: SequenceStep, index: number): string {
  return step.label?.trim() || `Step ${index + 1}`;
}

/**
 * What a step does, without its name or its continuation rule.
 *
 * Shared by the prose panel and the timeline's collapsed card so the two cannot
 * describe the same step differently — the whole reason the sequence is unreadable
 * today is that its summary (`Call ×2`) says less than the step does.
 */
export function describeStepAction(step: SequenceStep): string {
  const parts = [`${verb(step, 's')} ${describeRepeat(step)}`];

  if (step.channel === 'voice' && step.onMachine.kind === 'leave_message') {
    parts.push('leaving a message if a machine answers');
  }
  // `timing`, when set, replaces `timeOfDay` at runtime; its own sentence says when.
  if (step.channel === 'voice' && !step.timing && step.timeOfDay === 'rotate') {
    parts.push('varying the time of day between attempts');
  }

  return parts.join(', ');
}

/**
 * The one-line summary under a collapsed step in the timeline.
 *
 * Deliberately includes the continuation rule: "carries on if nobody picks up"
 * is the single fact an author most needs and the one today's rail omits.
 */
export function describeStepLine(step: SequenceStep, index: number, isLast: boolean): string {
  const action = describeStepAction(step);
  if (isLast) return `${action} — then the run ends either way`;
  if (step.continueOn.length === 0) return `${action} — the run always ends here`;
  return `${action} — carries on if ${outcomeWords(step.continueOn)}`;
}

function describeSequence(spec: CampaignSpec): CampaignSentence[] {
  const sentences: CampaignSentence[] = [];

  spec.sequence.forEach((step, index) => {
    const isLast = index === spec.sequence.length - 1;
    const name = stepName(step, index);

    sentences.push({
      id: `step.${step.id}`,
      text: `${name}: ${describeTriggerPhrase(step)}, ${describeStepAction(step)}.`,
    });

    const timing = describeTiming(step, spec.window);
    if (timing) sentences.push({ id: `step.${step.id}.timing`, text: timing });

    if (step.onCallbackRequested === 'use_requested_time') {
      sentences.push({
        id: `step.${step.id}.callback`,
        text: `If they ask to be called at a particular time, that replaces this step's own timing.`,
      });
    }

    if (isLast) {
      sentences.push({
        id: `step.${step.id}.end`,
        text: `That is the last step — the run finishes however it ends.`,
      });
      return;
    }

    const next = stepName(spec.sequence[index + 1]!, index + 1);

    // An empty `continueOn` mid-sequence is legal and almost certainly a
    // mistake, but this renderer does not editorialise — it states the
    // behaviour plainly and lets the editor raise it.
    sentences.push({
      id: `step.${step.id}.continue`,
      text:
        step.continueOn.length === 0
          ? `Nothing carries on to ${next}: the run always ends at this step.`
          : `It only carries on to ${next} if ${outcomeWords(step.continueOn)}. Anything else ends the run here.`,
    });

    // `next` is the default and what every step did before the field existed,
    // so only the departure from it is worth a sentence.
    if (step.afterLastTry === 'stop') {
      sentences.push({
        id: `step.${step.id}.afterLastTry`,
        text: `If its last try still does not reach them, the run ends here rather than going on to ${next}.`,
      });
    }
  });

  return sentences;
}

// ───────────────────────────── success & ending ─────────────────────────────

/**
 * Labels for the success conditions the editor can author. Shared by the picker
 * and by every summary, so the two cannot disagree about what a campaign's
 * success means.
 */
export const SUCCESS_LABELS: Record<SuccessCondition['kind'], string> = {
  appointment_booked: 'An appointment is booked',
  appointment_confirmed: 'The appointment is confirmed',
  outcome: 'The call ends a certain way',
  data_collected: 'Specific details are collected',
  any: 'Any of several conditions',
  all: 'All of several conditions',
};

/** The picker's summary: the label alone. */
export function describeSuccess(success: SuccessCondition): string {
  return SUCCESS_LABELS[success.kind] ?? success.kind;
}

/**
 * Success as a phrase that completes "It counts as a success when …".
 *
 * Recursive, because `any`/`all` nest. The nested forms are not authorable in
 * the editor today, but a spec carrying one must still describe itself — a
 * compiled or hand-written spec reaches this renderer too.
 */
function successPhrase(success: SuccessCondition): string {
  switch (success.kind) {
    case 'appointment_booked':
      return 'an appointment is booked';
    case 'appointment_confirmed':
      return 'the appointment is confirmed';
    case 'outcome':
      return outcomeWords(success.anyOf);
    case 'data_collected':
      return `we have collected ${joinList(success.fields, 'and')}`;
    case 'any':
      return joinList(success.of.map(successPhrase), 'or');
    case 'all':
      return joinList(success.of.map(successPhrase), 'and');
  }
}

function describeEnding(spec: CampaignSpec): CampaignSentence[] {
  const sentences: CampaignSentence[] = [
    {
      id: 'ending.success',
      text: `It counts as a success when ${successPhrase(spec.success)}.`,
    },
  ];

  if (spec.stopOn.length > 0) {
    sentences.push({
      id: 'ending.stop',
      text: `Whatever step it is on, the run stops as soon as ${outcomeWords(spec.stopOn)}.`,
    });
  }

  const cap = `It never makes more than ${plural(spec.maxTotalAttempts, 'call')} in total`;
  sentences.push({
    id: 'ending.limits',
    text:
      spec.expiresAfterDays == null
        ? `${cap}, and has no time limit.`
        : `${cap}, and gives up after ${plural(spec.expiresAfterDays, 'day')}.`,
  });

  return sentences;
}

function describeWindowSentence(window: OutreachWindow): CampaignSentence {
  const base = `Calls only go out ${describeDaysLong(window.days)}, between ${window.start} and ${window.end}`;
  return {
    id: 'hours',
    text: window.useLocationHours ? `${base}, and only while the location is open.` : `${base}.`,
  };
}

/** `Mon–Fri, 09:00–17:00` — the compact form, for a collapsed row. */
export function describeWindow(window: OutreachWindow): string {
  const suffix = window.useLocationHours ? ', inside opening hours' : '';
  return `${describeDays(window.days)}, ${window.start}–${window.end}${suffix}`;
}

/** `6 calls · gives up after 30 days` — the compact form, for a collapsed row. */
export function describeLimits(spec: CampaignSpec): string {
  const expiry =
    spec.expiresAfterDays == null
      ? 'never gives up'
      : `gives up after ${plural(spec.expiresAfterDays, 'day')}`;
  return `${plural(spec.maxTotalAttempts, 'call')} · ${expiry}`;
}

// ───────────────────────────── assembly ─────────────────────────────

export interface CampaignSentence {
  /**
   * Which control owns this clause. The panel scrolls to it on click, and the
   * compiler's change summary names it. Stable across edits that do not change
   * the shape of the spec — step clauses key off the step id, which is itself
   * stable across reordering.
   */
  id: string;
  text: string;
}

export type CampaignSectionId = 'audience' | 'sequence' | 'ending' | 'hours';

export interface CampaignSection {
  id: CampaignSectionId;
  /** The band heading, matching the editor's own vocabulary. */
  heading: string;
  sentences: CampaignSentence[];
}

/**
 * The whole campaign as four sections of short sentences.
 *
 * The order is the order the editor lays the campaign out in — who, what
 * happens, when it ends, when we may call — so reading the panel and reading
 * the timeline are the same act.
 */
export function describeCampaign(
  spec: CampaignSpec,
  options: DescribeOptions = {}
): CampaignSection[] {
  return [
    {
      id: 'audience',
      heading: 'Who we call',
      sentences: describeAudience(
        spec.audience,
        options,
        options.asChecked === true && campaignPlacesCalls(spec)
      ),
    },
    { id: 'sequence', heading: 'What happens', sentences: describeSequence(spec) },
    { id: 'ending', heading: 'When it ends', sentences: describeEnding(spec) },
    { id: 'hours', heading: 'When we may call', sentences: [describeWindowSentence(spec.window)] },
  ];
}

/**
 * The campaign as plain text — one paragraph per section.
 *
 * This is what the author edits and what the compiler is given, so it carries
 * no markup and no ids: anything that survives the round trip has to be
 * something a person would type.
 */
export function campaignText(spec: CampaignSpec, options: DescribeOptions = {}): string {
  // As STORED, whatever the caller asked for: see `DescribeOptions.asChecked`.
  return describeCampaign(spec, { ...options, asChecked: false })
    .map((section) => section.sentences.map((s) => s.text).join(' '))
    .join('\n\n');
}

/** One clause that differs between two versions of a campaign. */
export interface CampaignChange {
  /** The sentence id, so the editor can open the control that moved. */
  id: string;
  section: CampaignSectionId;
  kind: 'added' | 'removed' | 'changed';
  /** Absent when the clause did not exist before. */
  before?: string;
  /** Absent when the clause no longer exists. */
  after?: string;
}

/**
 * What changed between two campaigns, in the same sentences the author reads.
 *
 * **A diff of the descriptions, not of the specs.** Two properties follow, and
 * both are the reason this is not a field-by-field object diff. It cannot
 * report a change the author has no words for — a spec difference with no
 * sentence is invisible here, which is a real limitation and the price of the
 * second property: everything it does report is phrased in the vocabulary
 * already on screen, with no second translation layer to drift.
 *
 * Used after the compiler applies an edit. The author asked for one thing in
 * prose; this is the list of everything that actually moved, including what
 * they did not ask for — which is what makes applying compiled changes directly
 * safe enough to do without a blocking review.
 */
export function diffCampaign(
  before: CampaignSpec,
  after: CampaignSpec,
  options: DescribeOptions = {}
): CampaignChange[] {
  const index = (spec: CampaignSpec): Map<string, { text: string; section: CampaignSectionId }> => {
    const map = new Map<string, { text: string; section: CampaignSectionId }>();
    // As STORED: a diff is of what the author changed (`DescribeOptions.asChecked`).
    for (const section of describeCampaign(spec, { ...options, asChecked: false })) {
      for (const sentence of section.sentences) {
        map.set(sentence.id, { text: sentence.text, section: section.id });
      }
    }
    return map;
  };

  const from = index(before);
  const to = index(after);
  const changes: CampaignChange[] = [];

  // `after` order first, so the list reads in the order the campaign does.
  for (const [id, next] of to) {
    const previous = from.get(id);
    if (!previous) {
      changes.push({ id, section: next.section, kind: 'added', after: next.text });
    } else if (previous.text !== next.text) {
      changes.push({
        id,
        section: next.section,
        kind: 'changed',
        before: previous.text,
        after: next.text,
      });
    }
  }

  for (const [id, previous] of from) {
    if (!to.has(id)) {
      changes.push({ id, section: previous.section, kind: 'removed', before: previous.text });
    }
  }

  return changes;
}

/**
 * How many attempts the sequence will make if every step runs to completion.
 *
 * Not part of the description — it is a derived fact, and this module states
 * only what the spec says. The editor uses it to point out a cap that can never
 * be reached, which is legal, common, and almost always a mistake.
 */
export function plannedAttempts(spec: CampaignSpec): number {
  return spec.sequence.reduce((total, step) => total + step.repeat.times, 0);
}

/** Minutes a step spends on its own repeats, after its first attempt. */
function internalSpread(step: SequenceStep): number {
  return Math.max(0, step.repeat.times - 1) * step.repeat.spacingMinutes;
}

/**
 * Minutes from qualification to the first attempt of each step.
 *
 * A LOWER BOUND, and every caller has to say so: it assumes every step runs,
 * every attempt resolves the instant it is made, and the calling window pushes
 * nothing. The simulator is what answers the same question against a real
 * clock. Being approximate is fine — the editor's time axis exists to show the
 * shape of a sequence, not to promise a timetable — but being silently
 * approximate would not be.
 *
 * A step's own repeats push the *next* step out, which is the part that is easy
 * to get wrong by hand: two calls four hours apart followed by a one-day wait
 * puts the next step 28 hours in, not 24.
 */
export function stepStartOffsets(spec: CampaignSpec): number[] {
  const offsets: number[] = [];
  let cursor = 0;

  spec.sequence.forEach((step, index) => {
    // The first step starts on qualification; every other one waits.
    if (index > 0) cursor += step.trigger.kind === 'after_previous' ? step.trigger.minutes : 0;
    offsets.push(cursor);
    cursor += internalSpread(step);
  });

  return offsets;
}

/**
 * The soonest the sequence can finish, in minutes from qualification.
 *
 * Derived from {@link stepStartOffsets} rather than computed alongside it: the
 * axis the editor draws and the total it prints underneath must agree, and two
 * cursors walking the same sequence is exactly how they stop agreeing.
 */
export function plannedSpanMinutes(spec: CampaignSpec): number {
  const offsets = stepStartOffsets(spec);
  const lastIndex = spec.sequence.length - 1;
  if (lastIndex < 0) return 0;
  return offsets[lastIndex]! + internalSpread(spec.sequence[lastIndex]!);
}
